import "../styles/CitySelector.css";
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCities } from '../providers/CitiesContext';

const MIN_CITY_BUTTON_WIDTH = 96;
const MAX_CITY_BUTTON_WIDTH = 116;
const CITY_BUTTON_GAP = 4;
const CITY_STEP = 3;

const classForCity = (cityAvailability, cityKey) => {
  const key = String(cityKey).toLowerCase();
  if (cityAvailability[key] === true) return "has-data";
  if (cityAvailability[key] === false) return "no-data";
  return "unknown-data";
};

const titleForCity = (cityAvailability, cityKey) => {
  const key = String(cityKey).toLowerCase();
  if (cityAvailability[key] === true) return "Есть активные ЖК";
  if (cityAvailability[key] === false) return "Нет активных ЖК";
  return "Проверка...";
};

function CitySelector({ selectedCity, setSelectedCity, cityAvailability = {} }) {
  const { cities, isCitiesLoading, citiesError } = useCities();
  const visibleCities = useMemo(
    () => cities.filter((city) => city.isActive !== false),
    [cities]
  );
  const [cityStartIndex, setCityStartIndex] = useState(0);
  const [maxCityStartIndex, setMaxCityStartIndex] = useState(0);
  const [carouselSize, setCarouselSize] = useState({
    buttonWidth: MAX_CITY_BUTTON_WIDTH,
    viewportWidth: MAX_CITY_BUTTON_WIDTH,
    visibleSlots: 1,
  });
  const cityScrollZoneRef = useRef(null);
  const cityStartIndexRef = useRef(0);

  const measureCarousel = useCallback((availableWidth) => {
    const safeAvailableWidth = Math.max(MIN_CITY_BUTTON_WIDTH, Math.floor(availableWidth));
    const minSlot = MIN_CITY_BUTTON_WIDTH + CITY_BUTTON_GAP;
    const slotCapacity = Math.max(1, Math.floor((safeAvailableWidth + CITY_BUTTON_GAP) / minSlot));
    const visibleSlots = Math.min(Math.max(visibleCities.length, 1), slotCapacity);
    const rawButtonWidth = Math.floor(
      (safeAvailableWidth - CITY_BUTTON_GAP * (visibleSlots - 1)) / visibleSlots
    );
    const buttonWidth = Math.min(
      MAX_CITY_BUTTON_WIDTH,
      Math.max(MIN_CITY_BUTTON_WIDTH, rawButtonWidth)
    );
    const nextViewportWidth = (visibleSlots * buttonWidth) + (visibleSlots - 1) * CITY_BUTTON_GAP;
    const nextMaxIndex = Math.max(0, visibleCities.length - visibleSlots);
    const nextIndex = Math.min(cityStartIndexRef.current, nextMaxIndex);

    setCarouselSize({
      buttonWidth,
      viewportWidth: nextViewportWidth,
      visibleSlots,
    });
    setMaxCityStartIndex(nextMaxIndex);
    setCityStartIndex(nextIndex);
    cityStartIndexRef.current = nextIndex;
  }, [visibleCities.length]);

  const moveCities = (step) => {
    setCityStartIndex((prev) => {
      const next = prev + step;
      return Math.max(0, Math.min(maxCityStartIndex, next));
    });
  };

  useEffect(() => {
    cityStartIndexRef.current = cityStartIndex;
  }, [cityStartIndex]);

  useEffect(() => {
    const zoneEl = cityScrollZoneRef.current;
    if (!zoneEl) return undefined;

    const updateWidth = () => {
      measureCarousel(zoneEl.clientWidth);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(zoneEl);

    return () => resizeObserver.disconnect();
  }, [measureCarousel]);

  const cityTrackOffset = cityStartIndex * (carouselSize.buttonWidth + CITY_BUTTON_GAP);

  if (isCitiesLoading && visibleCities.length === 0) return <div className="city-selector city-selector-status">Загрузка городов…</div>;
  if (citiesError && visibleCities.length === 0) return <div className="city-selector city-selector-status">Города временно недоступны</div>;
  if (visibleCities.length === 0) return <div className="city-selector city-selector-status">Нет доступных городов</div>;

  return (
    <div
      className="city-selector"
      style={{
        "--city-button-width": `${carouselSize.buttonWidth}px`,
        "--city-scroll-viewport-width": `${carouselSize.viewportWidth}px`,
        "--city-button-gap": `${CITY_BUTTON_GAP}px`,
      }}
    >
      <button
        type="button"
        className="city-scroll-arrow city-scroll-arrow-left"
        onClick={() => moveCities(-CITY_STEP)}
        disabled={cityStartIndex === 0}
        aria-label="Прокрутить города влево"
      >
        ‹
      </button>

      <div className="city-scroll-zone" ref={cityScrollZoneRef}>
        <div className="city-carousel-viewport">
          <div
            className="city-carousel-track"
            style={{ transform: `translateX(-${cityTrackOffset}px)` }}
          >
            {visibleCities.map(city => (
              <button
                key={city.key}
                type="button"
                onClick={() => setSelectedCity(city.key)}
                className={[
                  "city-button",
                  selectedCity === city.key ? "active" : "",
                  classForCity(cityAvailability, city.key)
                ].join(" ").trim()}
                title={titleForCity(cityAvailability, city.key)}
              >
                {city.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="city-scroll-arrow city-scroll-arrow-right"
        onClick={() => moveCities(CITY_STEP)}
        disabled={cityStartIndex >= maxCityStartIndex}
        aria-label="Прокрутить города вправо"
      >
        ›
      </button>
    </div>
  );
}

export function MoreCitiesMenu({ selectedCity, setSelectedCity, cityAvailability = {} }) {
  const { cities } = useCities();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const moreCitiesRef = useRef(null);
  const searchInputRef = useRef(null);

  const visibleCities = useMemo(
    () => cities.filter((city) => city.isActive !== false),
    [cities]
  );
  const filteredCities = visibleCities.filter(city =>
    city.name.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (city) => {
    setSelectedCity(city.key);
    setIsOpen(false);
    setQuery("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreCitiesRef.current && !moreCitiesRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const frameId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen]);

  if (visibleCities.length === 0) return null;

  return (
    <div className="more-cities" ref={moreCitiesRef}>
      <button
        type="button"
        className="city-button more-cities-button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span>Ещё города</span>
        <span aria-hidden="true">▾</span>
      </button>

      {isOpen && (
        <div className="more-cities-menu" role="dialog" aria-label="Все города">
          
          <input
            ref={searchInputRef}
            className="more-cities-search"
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Найти город..."
          />
          {filteredCities.length > 0 ? (
            <div className="more-cities-grid">
              {filteredCities.map(city => (
                <button
                  key={city.key}
                  type="button"
                  className={[
                    "city-button",
                    "more-cities-item",
                    selectedCity === city.key ? "active" : "",
                    classForCity(cityAvailability, city.key)
                  ].join(" ").trim()}
                  title={titleForCity(cityAvailability, city.key)}
                  onClick={() => handleSelect(city)}
                >
                  {city.name}
                </button>
              ))}
            </div>
          ) : (
            <div className="more-cities-empty">Город не найден</div>
          )}
        </div>
      )}
    </div>
  );
}

export default CitySelector;
